import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { knowledgeBaseService } from "@/services/knowledgeBaseService";
import { chatThreadsService } from "@/services/chatThreadsService";
import { mastermindSpacesService } from "@/services/mastermindSpacesService";
import { workspacesService } from "@/services/workspacesService";
import { topicsService } from "@/services/topicsService";
import {
  DEFAULT_MCP_GRANTS,
  MCP_COMMUNICATION_SOURCES,
  type McpCategory,
  type McpCommunicationSource,
  type McpGrants,
  type McpLenses,
  type McpReads,
  type McpWrites,
} from "@shared/mcpGrants";
import { supabase } from "@/lib/supabase";

const ITEM_CATEGORIES: Array<{
  key: Exclude<McpCategory, "communications" | "github">;
  label: string;
  hint: string;
}> = [
  { key: "documents", label: "Documents", hint: "Library documents" },
  { key: "threads", label: "Chat threads", hint: "Conversations in Your Chats" },
  { key: "spaces", label: "Spaces", hint: "Mastermind spaces" },
  { key: "workspaces", label: "Workspaces", hint: "Team workspaces you belong to" },
  { key: "topics", label: "Topics", hint: "Topics inside your spaces" },
];

const COMM_LABELS: Record<McpCommunicationSource, string> = {
  gmail: "Gmail",
  fireflies: "Fireflies",
  clickup: "ClickUp",
  telegram: "Telegram",
  gohighlevel: "GoHighLevel",
  zoom: "Zoom",
  ringcentral: "RingCentral",
  google_calendar: "Google Calendar",
};

type PickerItem = { id: string; label: string };

async function loadPickerCatalog(): Promise<Record<Exclude<McpCategory, "communications" | "github">, PickerItem[]>> {
  const [personalDocs, workspaces, personalSpaces, threads, topics] = await Promise.all([
    knowledgeBaseService.getKnowledgeBaseSummaries({ limit: 400 }),
    workspacesService.getWorkspaces(),
    mastermindSpacesService.getMastermindSpaces(),
    chatThreadsService.getAllOwnedThreadSummaries("all"),
    topicsService.getAllTopics(),
  ]);

  const [workspaceDocs, workspaceSpaces] = await Promise.all([
    Promise.all(
      workspaces.map((w) => knowledgeBaseService.getKnowledgeBaseSummaries({ workspaceId: w.id, limit: 400 }))
    ),
    Promise.all(workspaces.map((w) => mastermindSpacesService.getMastermindSpaces(w.id))),
  ]);

  const docsById = new Map<string, PickerItem>();
  for (const d of [...personalDocs, ...workspaceDocs.flat()]) {
    docsById.set(d.id, { id: d.id, label: d.title || "Untitled document" });
  }
  const spacesById = new Map<string, PickerItem>();
  for (const s of [...personalSpaces, ...workspaceSpaces.flat()]) {
    spacesById.set(s.id, { id: s.id, label: s.name || "Untitled space" });
  }

  return {
    documents: [...docsById.values()],
    threads: threads.map((t) => ({ id: t.id, label: t.title || "Untitled thread" })),
    spaces: [...spacesById.values()],
    workspaces: workspaces.map((w) => ({ id: w.id, label: w.name || "Untitled workspace" })),
    topics: topics.map((t) => ({ id: t.id, label: t.name || "Untitled topic" })),
  };
}

function ItemPicker({
  items,
  selected,
  onChange,
}: {
  items: PickerItem[];
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((i) => i.label.toLowerCase().includes(needle) || i.id.toLowerCase().includes(needle));
  }, [items, q]);
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const visibleIds = filtered.map((i) => i.id);
  const allVisibleChecked = visibleIds.length > 0 && visibleIds.every((id) => selectedSet.has(id));

  return (
    <div className="space-y-2">
      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search…"
        className="h-8 text-sm"
      />
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {selected.length} selected
          {items.length ? ` of ${items.length}` : ""}
        </span>
        {filtered.length > 0 && (
          <button
            type="button"
            className="underline-offset-2 hover:underline"
            onClick={() => {
              if (allVisibleChecked) {
                const drop = new Set(visibleIds);
                onChange(selected.filter((id) => !drop.has(id)));
              } else {
                onChange([...new Set([...selected, ...visibleIds])]);
              }
            }}
          >
            {allVisibleChecked ? "Deselect visible" : "Select visible"}
          </button>
        )}
      </div>
      <ScrollArea className="h-40 rounded-md border">
        <div className="p-2 space-y-1">
          {filtered.length === 0 ? (
            <p className="text-xs text-muted-foreground px-1 py-2">No items match.</p>
          ) : (
            filtered.map((item) => (
              <label key={item.id} className="flex items-center gap-2 rounded px-1 py-1 hover:bg-muted/60 cursor-pointer">
                <Checkbox
                  checked={selectedSet.has(item.id)}
                  onCheckedChange={(checked) => {
                    if (checked === true) onChange([...selected, item.id]);
                    else onChange(selected.filter((id) => id !== item.id));
                  }}
                />
                <span className="text-sm truncate">{item.label}</span>
              </label>
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

export function McpDataGrantEditor({
  value,
  onChange,
}: {
  value: McpGrants;
  onChange: (next: McpGrants) => void;
}) {
  const grants = value.categories ? value : DEFAULT_MCP_GRANTS;
  const catalogQuery = useQuery({
    queryKey: ["mcp-grant-picker-catalog"],
    queryFn: loadPickerCatalog,
    staleTime: 60_000,
  });
  const catalog = catalogQuery.data;
  const githubQuery = useQuery({
    queryKey: ["mcp-grant-github-repos"],
    queryFn: async (): Promise<PickerItem[]> => {
      const { data } = await supabase.from("github_repo_chunks").select("owner, repo");
      const keys = [
        ...new Set(
          (data ?? []).map((r) => {
            const owner = typeof r.owner === "string" ? r.owner : "";
            const repo = typeof r.repo === "string" ? r.repo : "";
            return `${owner}/${repo}`;
          })
        ),
      ].filter((id) => id !== "/").sort();
      return keys.map((id) => ({ id, label: id }));
    },
    staleTime: 60_000,
  });

  const setCategory = (key: McpCategory, on: boolean) => {
    onChange({
      ...grants,
      categories: { ...grants.categories, [key]: on },
    });
  };

  const setAllowlist = (key: McpCategory, ids: string[] | null) => {
    onChange({
      ...grants,
      allowlists: { ...grants.allowlists, [key]: ids },
    });
  };

  const setLens = (key: keyof McpLenses, on: boolean) => {
    onChange({
      ...grants,
      lenses: { ...grants.lenses, [key]: on },
    });
  };

  const setRead = (key: keyof McpReads, on: boolean) => {
    onChange({
      ...grants,
      reads: { ...grants.reads, [key]: on },
    });
  };

  const setWrite = (key: keyof McpWrites, on: boolean) => {
    onChange({
      ...grants,
      writes: { ...grants.writes, [key]: on },
    });
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Choose what this connection can read. Turning a category off hides it from Claude, ChatGPT, and Gemini.
      </p>
      {ITEM_CATEGORIES.map((cat) => {
        const on = grants.categories[cat.key];
        const list = grants.allowlists?.[cat.key];
        const mode = Array.isArray(list) ? "choose" : "all";
        return (
          <div key={cat.key} className="rounded-lg border p-3 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-medium">{cat.label}</p>
                <p className="text-xs text-muted-foreground">{cat.hint}</p>
              </div>
              <Switch checked={on} onCheckedChange={(checked) => setCategory(cat.key, checked)} />
            </div>
            {on && (
              <>
                <RadioGroup
                  value={mode}
                  onValueChange={(v) => setAllowlist(cat.key, v === "all" ? null : [])}
                  className="flex gap-4"
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="all" id={`${cat.key}-all`} />
                    <Label htmlFor={`${cat.key}-all`} className="font-normal cursor-pointer">
                      All
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="choose" id={`${cat.key}-choose`} />
                    <Label htmlFor={`${cat.key}-choose`} className="font-normal cursor-pointer">
                      Choose items
                    </Label>
                  </div>
                </RadioGroup>
                {mode === "choose" && (
                  catalogQuery.isLoading ? (
                    <p className="text-xs text-muted-foreground">Loading {cat.label.toLowerCase()}…</p>
                  ) : catalogQuery.isError ? (
                    <p className="text-xs text-destructive">Could not load items.</p>
                  ) : (
                    <ItemPicker
                      items={catalog?.[cat.key] ?? []}
                      selected={list ?? []}
                      onChange={(ids) => setAllowlist(cat.key, ids)}
                    />
                  )
                )}
              </>
            )}
          </div>
        );
      })}

      <div className="rounded-lg border p-3 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-medium">Communications</p>
            <p className="text-xs text-muted-foreground">
            Indexed inbox content only (Gmail, Fireflies, ClickUp, Telegram, GoHighLevel, Zoom, RingCentral). Unindexed mail is not exposed.
            </p>
          </div>
          <Switch
            checked={grants.categories.communications}
            onCheckedChange={(checked) => setCategory("communications", checked)}
          />
        </div>
        {grants.categories.communications && (
          <>
            <RadioGroup
              value={Array.isArray(grants.allowlists?.communications) ? "choose" : "all"}
              onValueChange={(v) =>
                setAllowlist("communications", v === "all" ? null : [...MCP_COMMUNICATION_SOURCES])
              }
              className="flex gap-4"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="all" id="comm-all" />
                <Label htmlFor="comm-all" className="font-normal cursor-pointer">
                  All platforms
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="choose" id="comm-choose" />
                <Label htmlFor="comm-choose" className="font-normal cursor-pointer">
                  Choose platforms
                </Label>
              </div>
            </RadioGroup>
            {Array.isArray(grants.allowlists?.communications) && (
              <div className="space-y-2">
                {MCP_COMMUNICATION_SOURCES.map((src) => {
                  const selected = (grants.allowlists?.communications ?? []) as McpCommunicationSource[];
                  const checked = selected.includes(src);
                  return (
                    <label key={src} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(v) => {
                          const next = v === true ? [...selected, src] : selected.filter((s) => s !== src);
                          setAllowlist("communications", next);
                        }}
                      />
                      <span className="text-sm">{COMM_LABELS[src]}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      <div className="rounded-lg border p-3 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-medium">GitHub repos</p>
            <p className="text-xs text-muted-foreground">
              Indexed repository chunks, AST symbols, and graph neighbors — never the whole repo.
            </p>
          </div>
          <Switch
            checked={grants.categories.github !== false}
            onCheckedChange={(checked) => setCategory("github", checked)}
          />
        </div>
        {grants.categories.github !== false && (
          <>
            <RadioGroup
              value={Array.isArray(grants.allowlists?.github) ? "choose" : "all"}
              onValueChange={(v) => setAllowlist("github", v === "all" ? null : [])}
              className="flex gap-4"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="all" id="github-all" />
                <Label htmlFor="github-all" className="font-normal cursor-pointer">
                  All indexed repos
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="choose" id="github-choose" />
                <Label htmlFor="github-choose" className="font-normal cursor-pointer">
                  Choose repos
                </Label>
              </div>
            </RadioGroup>
            {Array.isArray(grants.allowlists?.github) && (
              githubQuery.isLoading ? (
                <p className="text-xs text-muted-foreground">Loading repos…</p>
              ) : (
                <ItemPicker
                  items={githubQuery.data ?? []}
                  selected={grants.allowlists?.github ?? []}
                  onChange={(ids) => setAllowlist("github", ids)}
                />
              )
            )}
          </>
        )}
      </div>

      <div className="rounded-lg border p-3 space-y-3">
        <p className="text-sm font-medium">Librarian lenses</p>
        <p className="text-xs text-muted-foreground">
          Short retrieval riders — not full essays. GCC/Faith instruction text is edited on the connections page.
        </p>
        {(
          [
            ["sourceOfTruth", "Source of Truth pointers"],
            ["personalDna", "Personal DNA slice"],
            ["faith", "Faith lens"],
            ["gcc", "GCC lens"],
          ] as Array<[keyof McpLenses, string]>
        ).map(([key, label]) => (
          <label key={key} className="flex items-center justify-between gap-2">
            <span className="text-sm">{label}</span>
            <Switch checked={grants.lenses?.[key] !== false} onCheckedChange={(v) => setLens(key, v)} />
          </label>
        ))}
      </div>

      <div className="rounded-lg border border-amber-500/40 p-3 space-y-3">
        <p className="text-sm font-medium">Live Google access</p>
        <p className="text-xs text-muted-foreground">
          Unlike the indexed Gmail and Calendar data above, these read your mailbox and calendar live. Off unless you
          turn them on for this connection. They need Google connected for AI assistants in the Gmail plugin.
        </p>
        {(
          [
            ["gmailRead", "Read Gmail", "Adds gmail_list_messages and gmail_get_message. Message text is untrusted."],
            ["calendarRead", "Read Google Calendar", "Adds calendar_list_calendars and calendar_list_events."],
          ] as Array<[keyof McpReads, string, string]>
        ).map(([key, label, hint]) => (
          <label key={key} className="flex items-start justify-between gap-3">
            <span>
              <span className="block text-sm">{label}</span>
              <span className="block text-xs text-muted-foreground">{hint}</span>
            </span>
            <Switch checked={grants.reads?.[key] === true} onCheckedChange={(v) => setRead(key, v)} />
          </label>
        ))}
      </div>

      <div className="rounded-lg border border-amber-500/40 p-3 space-y-3">
        <p className="text-sm font-medium">Write actions</p>
        <p className="text-xs text-muted-foreground">
          Everything above is read-only. These let the AI client change things in your connected apps, so they are off
          unless you turn them on for this connection.
        </p>
        <label className="flex items-start justify-between gap-3">
          <span>
            <span className="block text-sm">ClickUp task tools</span>
            <span className="block text-xs text-muted-foreground">
              Adds clickup_create_task, plus read-only clickup_list_lists and clickup_get_task (live ClickUp reads). Tasks are created with your own ClickUp connection when you have
              connected one, otherwise with the shared workspace key.
            </span>
          </span>
          <Switch
            checked={grants.writes?.clickupCreateTask === true}
            onCheckedChange={(v) => setWrite("clickupCreateTask", v)}
          />
        </label>
        {(
          [
            ["gmailDraft", "Draft Gmail messages", "Adds gmail_create_draft. Creates drafts only; nothing is sent."],
            [
              "gmailSend",
              "Send Gmail drafts",
              "Adds gmail_send_draft (irreversible) and lets calendar_create_event email invitations. Only drafts created through this connection can be sent, unchanged. Daily limit applies.",
            ],
            ["calendarCreateEvent", "Create Google Calendar events", "Adds calendar_create_event. Attendees are not emailed unless sending is also on."],
          ] as Array<[keyof McpWrites, string, string]>
        ).map(([key, label, hint]) => (
          <label key={key} className="flex items-start justify-between gap-3">
            <span>
              <span className="block text-sm">{label}</span>
              <span className="block text-xs text-muted-foreground">{hint}</span>
            </span>
            <Switch checked={grants.writes?.[key] === true} onCheckedChange={(v) => setWrite(key, v)} />
          </label>
        ))}
      </div>
    </div>
  );
}
